/**
 * SelectOKRModal Component
 * Modal for selecting a target OKR when duplicating a Key Result
 */

import React, { useState, useMemo } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useOKRs } from '../../hooks';
import { Search } from 'lucide-react';

interface SelectOKRModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (okrId: string) => void;
  isLoading?: boolean;
}

export const SelectOKRModal: React.FC<SelectOKRModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOkrId, setSelectedOkrId] = useState<string | null>(null);

  // Fetch all active OKRs (excluding completed, cancelled, deleted)
  const { data: okrsData, isLoading: isLoadingOKRs } = useOKRs({
    statuses: ['draft', 'active', 'on_track', 'at_risk', 'off_track'],
    limit: 100
  });

  const okrs = okrsData?.okrs || [];

  // Filter OKRs based on search term
  const filteredOKRs = useMemo(() => {
    if (!searchTerm.trim()) {
      return okrs;
    }

    const term = searchTerm.toLowerCase();
    return okrs.filter(
      (okr) =>
        okr.title.toLowerCase().includes(term) ||
        okr.description?.toLowerCase().includes(term) ||
        `${okr.owner.firstName} ${okr.owner.lastName}`.toLowerCase().includes(term) ||
        okr.type.toLowerCase().includes(term)
    );
  }, [okrs, searchTerm]);

  const handleSelect = () => {
    if (selectedOkrId) {
      onSelect(selectedOkrId);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedOkrId(null);
    onClose();
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      company: 'bg-purple-100 text-purple-800',
      department: 'bg-blue-100 text-blue-800',
      team: 'bg-green-100 text-green-800',
      individual: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-stone-100 text-stone-800';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Select Target OKR"
      size="lg"
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            type="text"
            placeholder="Search OKRs by title, owner, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* OKR List */}
        <div className="border border-stone-200 rounded-lg overflow-hidden">
          {isLoadingOKRs ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : filteredOKRs.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              {searchTerm ? 'No OKRs found matching your search' : 'No active OKRs available'}
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {filteredOKRs.map((okr) => (
                <button
                  key={okr.id}
                  onClick={() => setSelectedOkrId(okr.id)}
                  className={`w-full text-left p-4 border-b border-stone-100 transition-colors hover:bg-stone-50 ${
                    selectedOkrId === okr.id ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-stone-900 mb-1">{okr.title}</h4>
                      {okr.description && (
                        <p className="text-sm text-stone-500 mb-2 line-clamp-2">
                          {okr.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(
                            okr.type
                          )}`}
                        >
                          {okr.type}
                        </span>
                        <span className="text-xs text-stone-500">
                          Owner: {okr.owner.firstName} {okr.owner.lastName}
                        </span>
                        {okr.owner.department && (
                          <span className="text-xs text-stone-500">
                            {okr.owner.department}
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedOkrId === okr.id && (
                      <div className="ml-4">
                        <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSelect}
            disabled={!selectedOkrId || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center">
                <LoadingSpinner size="sm" className="mr-2" />
                Copying...
              </span>
            ) : (
              'Copy to Selected OKR'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
